import type { HivePluginOptions } from '../player.interface';
import type { VideoJsPlayer } from 'video.js';

import videojs from 'video.js';

import { HiveErrorMessage, HivePlugin, HiveSeverity, OptionsHtml5 } from './hive.interface';
import { loadScript } from '../../../utils/load-script';
import { ErrorCode, PlayerError } from '../player-errors';
import { SourceType } from '../player.config';
import { VideoJsNativeEvent } from '../videojs-event';

let plugin: HivePlugin | undefined;

export async function getHiveSources(
  player: VideoJsPlayer,
  hiveOptions: HivePluginOptions,
  abortSignal: AbortSignal
): Promise<videojs.Tech.SourceObject[]> {
  // Abort if the signal is already aborted
  if (abortSignal.aborted) {
    return Promise.reject();
  }

  if (!window.HiveHtml5 || !window.HiveModule) {
    await Promise.all([
      loadScript(
        'https://media-players.hivestreaming.com/plugins/html5/11.2.0/html5.java.hivejs.hive.min.js',
        'mi-hivejs-sdk'
      ),
      loadScript(
        'https://media-players.hivestreaming.com/common_libs/html5/videojs/hive-module.js',
        'mi-hivejs-videojs-connector'
      ),
    ]);
  }

  if (!plugin) {
    plugin = createPlugin(player, hiveOptions);
  }

  try {
    const { ticket, token } = hiveOptions;
    const value = token || ticket;

    if (!value) {
      return []; // TODO: we probably should throw an error here
    }

    const session = await plugin.initSession(value);

    window.HiveModule.enable(plugin, player, videojs);

    player.on(VideoJsNativeEvent.DISPOSE, () => plugin?.closeHiveSession());
    player.on(VideoJsNativeEvent.ERROR, () => plugin?.closeHiveSession());

    abortSignal.addEventListener('abort', () => plugin?.closeHiveSession(), { once: true });

    return [{ src: session.manifest, type: SourceType.HLS }];
  } catch (_) {
    // TODO: log the error
    destroyPlugin();
    throw new PlayerError(ErrorCode.PLAYER_ERR_INIT_HIVE_P2P_FAILED, 'The Hive plugin failed to initialize.');
  }
}

function destroyPlugin() {
  try {
    plugin?.destroyHivePlugin();
    plugin = undefined;
  } catch (_) {
    // In the unlikely scenario that the cleanup fails, we do nothing.
  }
}

function createPlugin(player: VideoJsPlayer, hiveOptions: HivePluginOptions): HivePlugin {
  const errorHandler = (msg: HiveErrorMessage) => {
    if (msg.severity === HiveSeverity.NORMAL) {
      // No action required
    } else if (msg.severity === HiveSeverity.HIGH) {
      // Session should be re-initialized, ideally with a re-trial mechanism
      plugin?.closeHiveSession();
      player.error('The Hive plugin has encountered a high severity error.');
    } else if (msg.severity === HiveSeverity.CRITICAL) {
      // Disable the Hive Plugin ( and fallback to the CDN if required)
      destroyPlugin();
      player.error('The Hive plugin has encountered a critical error.');
    }
  };

  const options: OptionsHtml5 = {
    playerName: 'videojs',
    playerVersion: videojs.VERSION,
    eventName: hiveOptions.eventName,
    debugLevel: hiveOptions.debugLevel,
    hiveTechOrder: hiveOptions?.techOrder,
    onError: errorHandler,
  };

  // backward compatibility for webcast
  const { ticket } = hiveOptions;
  if (ticket) {
    options.HiveJs = {
      streamUrlMatcher: [/https:\/\/.*(\/.*\.ts|\/.*\.m3u8|\/.*\.mp4)/],
    };
  }

  return new window.HiveHtml5(player.tech(false).el(), options);
}
